import { InnerCard } from 'views/components';
import { Loader } from 'mol-ui';
import PropTypes from 'prop-types';
import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { parseGender } from 'shared/utils';
import { patientActions } from 'core/Patient';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import {
    useDispatch,
    useSelector
} from 'hooks';
const {
    company: companyConfig,
    coverages,
    termsAndConditionsVersion
} = config;

const PatientInfo = ({
    id
}) => {
    
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        loading,
        selected: data
    } = useSelector('patient');

    useEffect(() => {
        if (!id) return;
        dispatch(patientActions.getPatientData(id));
    }, [id]);

    const {
        address,
        admission_date,
        birth_date,
        building_number,
        cellphone_number,
        city_name,
        company,
        district_name,
        door_number,
        email,
        family_burden,
        family_group_head,
        file_number,
        first_name,
        floor,
        gender,
        health_insurance,
        hr_head,
        identification,
        last_name,
        medical_coverage,
        plan,
        position,
        province_name,
        supervisor,
        terms_and_conditions,
        workplace
    } = data;

    const { name: companyName } = company ?? {};
    const supervisorName = supervisor ? `${ supervisor?.last_name }, ${ supervisor?.first_name } (${ supervisor?.identification })` : undefined;
    const age = !!birth_date ? moment().diff(moment(birth_date), 'years') : undefined;
    const age_months = !!birth_date ? moment().diff(moment(birth_date), 'months') : undefined;
    const age_string = `${ age }${ age <= 3 ? ` (${ age_months } ${ age_months === 1 ? t('mes') : t('meses') })` : '' }`;
    const birthDay = !!birth_date ? moment(birth_date).format('DD/MM/YYYY') : null;
    const name = last_name && first_name ? `${ last_name }, ${ first_name }` : null;
    const {
        accepted_at: tycAccepted,
        version: tycVersion
    } = terms_and_conditions || { accepted_at: undefined, version: undefined };

    if (loading) return <Loader active />;
    
    return (
        <InnerCard
            data={
                [{
                    field: 'full_name',
                    label: t('Nombre'),
                    value: name
                }, {
                    field: 'identification',
                    label: t('DNI'),
                    value: identification
                }, {
                    field: 'gender',
                    label: t('Sexo'),
                    value: parseGender(gender)
                }, {
                    field: 'birth_date',
                    label: t('Fecha de Nac.'),
                    value: birthDay
                }, {
                    field: 'age',
                    label: t('Edad'),
                    value: age ? age_string : undefined
                }, {
                    field: 'email',
                    label: t('Email'),
                    value: email
                }, {
                    field: 'cellphone_number',
                    label: t('Celular'),
                    value: cellphone_number
                }, {
                    field: 'address',
                    label: t('Dirección'),
                    value: address
                }, {
                    field: 'building_number',
                    label: t('Nº'),
                    value: building_number
                }, {
                    field: 'floor',
                    label: t('Piso'),
                    value: floor
                }, {
                    field: 'door_number',
                    label: t('Puerta'),
                    value: door_number
                }, {
                    field: 'province_name',
                    label: t('Provincia'),
                    value: province_name
                }, {
                    field: 'city_name',
                    label: t('Ciudad'),
                    value: city_name
                }, {
                    field: 'district_name',
                    label: t('Barrio'),
                    value: district_name

                }, ...(
                    coverages
                        ? [{
                            field: 'medical_coverage',
                            label: t('Cobertura'),
                            value: medical_coverage?.name
                        }, {
                            field: 'plan',
                            label: t('Plan'),
                            value: plan?.name
                        }, {
                            field: 'health_insurance',
                            label: t('Afiliado'),
                            value: health_insurance
                        }]
                        : []
                ), ...(
                    termsAndConditionsVersion !== '0'
                        ? [{
                            field: 'tyc_accepted',
                            label: t('Aceptados últimos TyC'),
                            value: tycVersion === termsAndConditionsVersion ? 'Si' : 'No'
                        }, {
                            field: 'tyc_timestamp',
                            label: t('Firma de TyC'),
                            value: tycAccepted ? moment(tycAccepted).format('DD/MM/YYYY HH:mm') : undefined
                        }]
                        : []
                ), {
                    label: t('Legajo'),
                    value: file_number,
                    visible: !!file_number
                }, {
                    label: t('Lugar de trabajo'),
                    value: workplace,
                    visible: !!workplace
                }, {
                    label: t('Cargo'),
                    value: position,
                    visible: !!position
                }, {
                    label: t('Carga de familia'),
                    value: family_burden,
                    visible: !!family_burden
                }, {
                    label: t('Jefe de RRHH'),
                    value: hr_head,
                    visible: !!hr_head
                }, {
                    label: t('Fecha de ingreso'),
                    value: admission_date,
                    visible: !!admission_date
                }, ...(
                    isEmpty(family_group_head) ? [] : [{
                        label: t('Titular'),
                        value: `${ family_group_head.patient_last_name }, ${ family_group_head.patient_first_name }`
                    }, {
                        label: `${ t('Celular') } (${ t('Titular') })`,
                        value: family_group_head.cellphone,
                        visible: !!family_group_head.cellphone
                    }, {
                        label: `${ t('Email') } (${ t('Titular') })`,
                        value: family_group_head.email,
                        visible: !!family_group_head.email
                    }]
                ), {
                    label: t('Supervisor'),
                    value: supervisorName,
                    visible: !!supervisorName
                }, {
                    label: t('Empresa'),
                    value: companyName,
                    visible: companyConfig
                }]
            }
            minCol="4"
        />
    );
};

PatientInfo.propTypes = {
    id: PropTypes.number
};

export default PatientInfo;
